import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GLOBAL_COMPANY_INFO } from '../lib/cms';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const serviceSublinks = [
    { label: 'Indendørs Projekter', href: '/indendors-projekter' },
    { label: 'Udendørsprojekter', href: '/udendors-projekter' },
    { label: 'Maler i Nørresundby', href: '/maler-norresundby' },
    { label: 'Maler i Brønderslev', href: '/maler-bronderslev' },
    { label: 'Maler i Støvring', href: '/maler-stovring' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white text-slate-900 border-b border-slate-200 shadow-sm ${
        scrolled ? 'py-2 sm:py-3 shadow-md' : 'py-3 sm:py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-[#65237e] rounded-lg p-1">
            <img
              src={GLOBAL_COMPANY_INFO.logo_url}
              alt="Maler Carsten Janum logo"
              className="h-14 sm:h-16 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (min 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8 text-sm font-medium">
            <Link
              to="/"
              className={`transition-colors duration-200 hover:text-[#65237e] py-2 border-b-2 ${
                isActive('/') && location.pathname === '/'
                  ? 'border-[#65237e] text-[#65237e] font-semibold'
                  : 'border-transparent text-slate-700'
              }`}
            >
              Forside
            </Link>

            {/* Dropdown for Vi tilbyder / Ydelser */}
            <div
              className="relative group"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <Link
                to="/ydelser"
                className={`inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-[#65237e] py-2 border-b-2 ${
                  isActive('/ydelser') ||
                  isActive('/indendors-projekter') ||
                  isActive('/udendors-projekter') ||
                  isActive('/maler-norresundby') ||
                  isActive('/maler-bronderslev') ||
                  isActive('/maler-stovring')
                    ? 'border-[#65237e] text-[#65237e] font-semibold'
                    : 'border-transparent text-slate-700'
                }`}
              >
                <span>Vi tilbyder</span>
                <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180 text-[#65237e]' : ''}`} />
              </Link>

              {/* Dropdown menu */}
              <div
                className={`absolute top-full left-0 w-64 pt-2 transition-all duration-200 ${
                  servicesDropdownOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="bg-white rounded-xl shadow-2xl border border-slate-200 py-2 overflow-hidden">
                  <Link
                    to="/ydelser"
                    className="block px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[#65237e] hover:bg-slate-50"
                  >
                    Alle Ydelser Overview &rarr;
                  </Link>
                  <div className="h-px bg-slate-100 my-1" />
                  {serviceSublinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`block px-4 py-2.5 text-sm transition-colors hover:bg-slate-50 hover:text-[#65237e] ${
                        isActive(link.href) ? 'text-[#65237e] font-semibold bg-slate-50' : 'text-slate-700'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/om-os"
              className={`transition-colors duration-200 hover:text-[#65237e] py-2 border-b-2 ${
                isActive('/om-os')
                  ? 'border-[#65237e] text-[#65237e] font-semibold'
                  : 'border-transparent text-slate-700'
              }`}
            >
              Om os
            </Link>

            <Link
              to="/projekter"
              className={`transition-colors duration-200 hover:text-[#65237e] py-2 border-b-2 ${
                isActive('/projekter') || isActive('/galleri')
                  ? 'border-[#65237e] text-[#65237e] font-semibold'
                  : 'border-transparent text-slate-700'
              }`}
            >
              Projekter
            </Link>

            <Link
              to="/kontakt"
              className={`transition-colors duration-200 hover:text-[#65237e] py-2 border-b-2 ${
                isActive('/kontakt')
                  ? 'border-[#65237e] text-[#65237e] font-semibold'
                  : 'border-transparent text-slate-700'
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Header Action Buttons (Phone & CTA) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href={`tel:${GLOBAL_COMPANY_INFO.phone_raw}`}
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-800 hover:text-[#65237e] transition-colors nowrap-contact px-3 py-2 rounded-lg hover:bg-slate-100"
              aria-label={`Ring til ${GLOBAL_COMPANY_INFO.phone}`}
            >
              <i className="fa-solid fa-phone text-[#65237e]" aria-hidden="true" />
              <span className="nowrap-contact">{GLOBAL_COMPANY_INFO.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#65237e] hover:bg-[#521b67] rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#65237e] focus:ring-offset-2"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile Hamburger Toggle (< 1200px) */}
          <div className="flex items-center gap-3 min-[1200px]:hidden">
            <a
              href={`tel:${GLOBAL_COMPANY_INFO.phone_raw}`}
              className="p-2 text-[#65237e] hover:bg-slate-100 rounded-lg transition-colors text-lg"
              aria-label="Ring op"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-800 hover:text-[#65237e] hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#65237e]"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-t border-slate-200 mt-3 px-4 pt-4 pb-6 shadow-2xl animate-in slide-in-from-top-2 duration-200 max-h-[85vh] overflow-y-auto">
          <nav className="flex flex-col gap-2 font-medium">
            <Link
              to="/"
              className={`p-3 rounded-lg text-base transition-colors ${
                isActive('/') && location.pathname === '/' ? 'bg-slate-100 text-[#65237e] font-semibold' : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              Forside
            </Link>

            {/* Collapsible Mobile Services */}
            <div>
              <button
                type="button"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between p-3 rounded-lg text-base text-slate-800 hover:bg-slate-50 transition-colors"
              >
                <span>Vi tilbyder</span>
                <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180 text-[#65237e]' : ''}`} />
              </button>

              {mobileServicesOpen && (
                <div className="ml-4 pl-3 border-l-2 border-[#65237e]/40 flex flex-col gap-1 my-1">
                  <Link
                    to="/ydelser"
                    className="p-2 text-sm text-[#65237e] font-semibold hover:bg-slate-50 rounded"
                  >
                    Oversigt over ydelser
                  </Link>
                  {serviceSublinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`p-2 text-sm rounded transition-colors ${
                        isActive(link.href) ? 'text-[#65237e] font-semibold bg-slate-100' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/om-os"
              className={`p-3 rounded-lg text-base transition-colors ${
                isActive('/om-os') ? 'bg-slate-100 text-[#65237e] font-semibold' : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              Om os
            </Link>

            <Link
              to="/projekter"
              className={`p-3 rounded-lg text-base transition-colors ${
                isActive('/projekter') || isActive('/galleri') ? 'bg-slate-100 text-[#65237e] font-semibold' : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              Projekter
            </Link>

            <Link
              to="/kontakt"
              className={`p-3 rounded-lg text-base transition-colors ${
                isActive('/kontakt') ? 'bg-slate-100 text-[#65237e] font-semibold' : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              Kontakt
            </Link>

            {/* Mobile Contact CTAs */}
            <div className="pt-4 mt-2 border-t border-slate-200 flex flex-col gap-3">
              <a
                href={`tel:${GLOBAL_COMPANY_INFO.phone_raw}`}
                className="flex items-center justify-center gap-2 p-3 text-sm font-semibold text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <i className="fa-solid fa-phone text-[#65237e]" />
                <span className="nowrap-contact">{GLOBAL_COMPANY_INFO.phone}</span>
              </a>

              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 p-3 text-sm font-semibold text-white bg-[#65237e] hover:bg-[#521b67] rounded-lg shadow-sm transition-colors text-center"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
